import React, {useCallback, useEffect, useLayoutEffect, useRef, useState,} from "react";
import '../../styles/components/boot-camp/boot-camp-page.scss';
import TabbedNavigation from "../tabbedNavigation/TabbedNavigation";
import DifficultyNavigation from "../difficultyNavigation/DifficultyNavigation";
import InfoModal from "../common/InfoModal";
import {getJsonItem, setItem} from "../../services/device/localStorage";
import {
  cardClicked,
  displayInfoModal,
  useBootCampPageViewModel
} from "../../services/bootcamp/bootcamp";
import LoadingAnimation2 from "../loadingAnimation/LoadingAnimation2";
import {FontIcon} from "../common/FontIcon";
import {DifficultyOption} from "../../models/view/common/DifficultyOption";
import debounce from "lodash/debounce"
import CourseSection from "./CourseSection/CourseSection";
import {ISectionModel} from "../../models/view/bootCampPage/IBootCampPageModel";
import ICard from "../../models/view/common/ICard";
import {CourseOverview} from "../courseOverview/CourseOverview";
import {useBootCampCourseId} from "../../factories/bootCampPageFactory";
import {courseViewedOnDeviceRepository} from "../../repositories/courses/course-viewed-on-device-repository";
import ILessonDetail from "../../models/view/common/ILessonDetail";
import {navigate} from "../../services/navigation/navigation";

interface IScrollData {
  scrollTop: number,
  windowWidth: number,
}

/**
 * save bootcamp scroll data outside of component to remove dep of ref
 * @param currentlySelectedDifficultyNavigationOption
 * @param scrollData
 */
function saveCurrentBootcampScrollData(currentlySelectedDifficultyNavigationOption: DifficultyOption, scrollData: IScrollData){
  const difficultyScrollPositionStorageKey = `bootcampScroll-${currentlySelectedDifficultyNavigationOption}`;
  setItem<IScrollData>(difficultyScrollPositionStorageKey, {...scrollData,});
}

/**
 * debounced save bootcamp scroll data outside of component to remove dep of ref
 */
const debouncedSaveCurrentBootcampScrollData = debounce(saveCurrentBootcampScrollData, 100);

export default function BootCampPage() {
  const bootCampPageViewModel = useBootCampPageViewModel();
  const {courseId} = useBootCampCourseId();
  const sectionElements = createSections(courseId, bootCampPageViewModel.sections, cardClicked);
  const isLoadingClassName = bootCampPageViewModel.isLoading ? `loading` : ``;
  const headerRef = useRef<HTMLDivElement>(document.createElement('div'));
  const scrollableContentRef = useRef<HTMLElement>(document.createElement('main'));

  let detailsOfLessons: ILessonDetail[] = [];

  bootCampPageViewModel.sections.forEach(function(s) {
    const sectionId = s.id;

    const sectionDetailsOfLessons = s.cards.map(({isComplete, difficultyCategory, number}) => {
      return {
        sectionId,
        isComplete,
        difficultyCategory,
        number,
      };
    });

    detailsOfLessons = [...detailsOfLessons, ...sectionDetailsOfLessons];
  });

  const [isCourseOverviewOpen, setIsCourseOverviewOpen] = useState<boolean>(false);

  // Manage marking of courses as having been viewed on the device
  // Delay display of the overview panel slightly so user is more likely to see the transition
  useEffect(() => {
    const timer = setTimeout(() =>{
      const hasOverviewForCourseBeenDisplayedOnDevice = courseViewedOnDeviceRepository.hasOverviewBeenDisplayed(courseId);
      if (!hasOverviewForCourseBeenDisplayedOnDevice) {
        // start first display
        setIsCourseOverviewOpen(true);
        // update storage to record it has been displayed
        courseViewedOnDeviceRepository.setDisplayedCourse(courseId);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [setIsCourseOverviewOpen, courseId]);

  // Scroll to the top of the view when the panel is opened
  useLayoutEffect(() => {
    if (isCourseOverviewOpen) {
      scrollableContentRef.current.scrollTo({top: 0, behavior: 'smooth'});
    }
  }, [isCourseOverviewOpen, scrollableContentRef]);

  const onClickOverviewToggleHandler = useCallback(
    () => setIsCourseOverviewOpen(current => !current),
    [setIsCourseOverviewOpen],
  );

  const loadingEl = bootCampPageViewModel.isLoading ? <LoadingAnimation2/> : null;

  const [shouldSkipNextScrollEvent, setShouldSkipNextScrollEvent] = useState(true);
  //wait for a second before caring about scroll events, since so many scroll events are fired when the page is loading.
  setTimeout(()=> setShouldSkipNextScrollEvent(false), 2000);

  useEffect(()=>{
    const difficultyScrollPositionStorageKey = `bootcampScroll-${bootCampPageViewModel.currentlySelectedDifficultyNavigationOption}`;
    const scrollData = getJsonItem<IScrollData>(difficultyScrollPositionStorageKey);

    //the scroll container retains scroll position between difficulties, so scroll to the top, unless we're loading, because we don't want to scroll up, then switch after the data loads.
    if(!scrollData && !bootCampPageViewModel.isLoading){
      scrollableContentRef.current.scrollTop = 0;
    }

    if(scrollData && scrollData.windowWidth === window.innerWidth){
      scrollableContentRef.current.scrollTop = scrollData.scrollTop;
    }
    if(scrollData && scrollData.windowWidth !== window.innerWidth){
      localStorage.removeItem(difficultyScrollPositionStorageKey);
    }
    function onWindowResize(){
      // cancel any pending debouncedSaveCurrentBootcampScrollData
      debouncedSaveCurrentBootcampScrollData.cancel();

      const scrollTop = scrollableContentRef.current.scrollTop;
      const windowWidth = window.innerWidth;
      saveCurrentBootcampScrollData(bootCampPageViewModel.currentlySelectedDifficultyNavigationOption, {scrollTop, windowWidth});
    }

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('deviceorientation', onWindowResize);

    return ()=>{
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('deviceorientation', onWindowResize);
    };
  }, [bootCampPageViewModel]);

  function onScroll(){
    if(shouldSkipNextScrollEvent) {
      // nothing to do, return to exit
      return;
    }
    // get scroll position before debounced call so that it can complete later even if ref goes away
    const scrollTop = scrollableContentRef.current.scrollTop;
    const windowWidth = window.innerWidth;
    // debounce saving scroll position on scroll to local storage
    debouncedSaveCurrentBootcampScrollData(bootCampPageViewModel.currentlySelectedDifficultyNavigationOption, {scrollTop, windowWidth,});
  }

  const infoModalEl = createInfoModalEl(bootCampPageViewModel.infoModal.isVisible);
  //we want to theme cards and navigation based on the difficulty level selected.
  const difficultyClassName = bootCampPageViewModel.currentlySelectedDifficultyNavigationOption;
  const featuredLessonEl = bootCampPageViewModel.featuredLesson ? createFeaturedLesson(bootCampPageViewModel.featuredLesson): null;
  const noAccessEl = !bootCampPageViewModel.hasAccessToBootcamp ? createNoAccessMessage() : null;
  const difficultyNavigationEl = bootCampPageViewModel.hasAccessToBootcamp
    ? <DifficultyNavigation
      difficultyNavigationOptions={bootCampPageViewModel.difficultyNavigationOptions}
      currentlySelectedDifficultyOption={bootCampPageViewModel.currentlySelectedDifficultyNavigationOption}
    />
    : null;
  const dynamicPanelToggleLabel = isCourseOverviewOpen ? 'Hide overview' : 'Show overview';
  return (
    <div className={`boot-camp-page ${difficultyClassName}`}>
      {infoModalEl}
      <header ref={headerRef} className="header">
        <div className={"section-title"}>
          <h1>Bootcamp</h1>
          <button className={"info-icon"} onClick={()=> displayInfoModal(true) }>
            {bootCampPageViewModel.hasAccessToBootcamp ? <FontIcon icon={"icon-info"}/> : null}
          </button>
        </div>
        {featuredLessonEl}
        {difficultyNavigationEl}
        <button
          className={`overview-toggle ${isCourseOverviewOpen ? `active` : ``}`}
          onClick={onClickOverviewToggleHandler}
          aria-label={dynamicPanelToggleLabel}
          title={dynamicPanelToggleLabel}
        >
          {isCourseOverviewOpen ? 'Close Overview' : 'Course Overview'}
          <FontIcon icon={"icon-angle-down"} />
        </button>
      </header>
      {loadingEl}
      <main ref={scrollableContentRef} className={`scrollable-content ${isLoadingClassName}`} onScroll={onScroll}>
        <CourseOverview
          courseId={courseId}
          isOverviewOpen={isCourseOverviewOpen}
          detailsOfLessons={detailsOfLessons}
        />
        {bootCampPageViewModel.hasAccessToBootcamp ? <div className="courseSections">{sectionElements}</div> : null}
        {noAccessEl}
      </main>
      <footer className="footer">
        <TabbedNavigation/>
      </footer>
    </div>
  );
}

function createFeaturedLesson({hashUrl, imgUrl, lessonName}: {hashUrl: string, imgUrl: string, lessonName: string}){
  return <button className={`featured-lesson`} onClick={()=> navigate(hashUrl)}>
    <img src={imgUrl} alt={"Featured lesson"}/>
    <div className={`title`}>{lessonName}</div>
    <FontIcon icon={"icon-arrow-right"}/>
  </button>
}

function createInfoModalEl(isVisible: boolean) {
  if (!isVisible) {
    return null;
  }

  return <InfoModal
    onClose={() => displayInfoModal(false)}
    title={"About Bootcamp"}
    icon={"icon-bootcamp"}
    text={<div>Welcome to the Bootcamp! Here you&apos;ll learn everything you
      need to play the piano, with lessons built from popular songs, organized by difficulty
      level, and arranged in order to ensure your learning
      is gradual and inevitable. The Bootcamp is the perfect place to begin your piano journey.</div>}
  />
}

function createSections(courseId: number, sections: ISectionModel[], cardClicked: (card: ICard) => void) {
  return sections.map((s, i) => <CourseSection
    courseId={courseId}
    sectionNumber={i + 1}
    key={`course-${courseId}-section-${i}`}
    sectionModel={s}
    onCardClick={(c) => cardClicked(c)}
    quantityOfNewSongs={s.quantityOfNewSongs}
    sectionId={s.id}
  />);
}

function createNoAccessMessage(){
  return <div className={`no-access`}>
    <FontIcon icon={"icon-bootcamp"}/>
    <div className={`message`}>Upgrade to a full membership for access to Bootcamp and special courses.</div>
  </div>
}

